// Text-to-speech settings + voice catalogue.
//
// Voices are device-specific (the set on an iPhone differs from a PC), so the
// preference is stored per-device in localStorage. We persist the voiceURI for
// an exact match plus the voiceName as a softer fallback across devices, then
// fall back to the language default. Engine = 'system' uses the browser's
// SpeechSynthesis; 'openvoice' is a placeholder for a future local bridge.

import { useEffect, useState } from 'react';
import type { Language } from './prefs.js';

export type TtsEngine = 'system' | 'elevenlabs' | 'openvoice';

export type SynthOutcome =
  | { ok: true; blob: Blob }
  // The server responded but couldn't synthesize — it already logged the reason.
  | { ok: false; kind: 'http'; status: number; error?: string; detail?: string }
  // The request never reached the server (offline, CORS, etc.) — client must log.
  | { ok: false; kind: 'network'; detail?: string };

/** POST text to the server's ElevenLabs route. */
export async function synthViaServer(text: string, lang: Language): Promise<SynthOutcome> {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('mdai_token') : null;
  let res: Response;
  try {
    res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/tts/speak`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ text, lang }),
    });
  } catch (e) {
    return { ok: false, kind: 'network', detail: e instanceof Error ? e.message : 'fetch_failed' };
  }
  if (!res.ok) {
    let error: string | undefined; let detail: string | undefined;
    try { const j = await res.json(); error = j?.error; detail = j?.detail; } catch { /* ignore */ }
    return { ok: false, kind: 'http', status: res.status, error, detail };
  }
  return { ok: true, blob: await res.blob() };
}

/** Tell the server we fell back to the system voice (cases it didn't see). */
export function reportTtsFallback(reason: string, detail?: string): void {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('mdai_token') : null;
  fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/tts/fallback`, {
    method: 'POST', keepalive: true,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ reason, detail }),
  }).catch(() => {});
}

export interface TtsServerStatus { configured: boolean; provider: string; voiceId: string | null }
export async function fetchTtsStatus(): Promise<TtsServerStatus> {
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('mdai_token') : null;
    const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/tts/status`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) return { configured: false, provider: 'elevenlabs', voiceId: null };
    return await res.json();
  } catch {
    return { configured: false, provider: 'elevenlabs', voiceId: null };
  }
}

export interface TtsSettings {
  engine: TtsEngine;
  engineExplicit?: boolean; // true once the user picks an engine on this device
  voiceURI: string | null;
  voiceName: string | null;
  rate: number;   // 0.5 .. 1.5
  pitch: number;  // 0 .. 2
}

export const DEFAULT_TTS: TtsSettings = {
  engine: 'system',
  engineExplicit: false,
  voiceURI: null,
  voiceName: null,
  rate: 1,
  pitch: 1,
};

// The twin's voice is configured for everyone: if the server has ElevenLabs set
// up (the cloned voice), all users default to it — including invited visitors who
// can't open voice settings. A per-device explicit choice still wins.
let serverTtsConfigured: boolean | null = null;
export async function primeTtsStatus(): Promise<void> {
  try { serverTtsConfigured = (await fetchTtsStatus()).configured; } catch { serverTtsConfigured = false; }
}
export function effectiveEngine(s: TtsSettings): TtsEngine {
  if (s.engineExplicit) return s.engine;
  return serverTtsConfigured ? 'elevenlabs' : 'system';
}

const KEY = 'mdai_tts';
const CHANGED = 'mdai-tts-changed';

export const BCP47: Record<Language, string> = { fr: 'fr-CA', en: 'en-US', es: 'es-ES' };

export function loadTts(): TtsSettings {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_TTS };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_TTS };
    return { ...DEFAULT_TTS, ...(JSON.parse(raw) as Partial<TtsSettings>) };
  } catch {
    return { ...DEFAULT_TTS };
  }
}

export function saveTts(s: TtsSettings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
    window.dispatchEvent(new CustomEvent(CHANGED));
  } catch { /* ignore */ }
}

/** Reactive settings hook for the controls UI. */
export function useTtsSettings(): [TtsSettings, (patch: Partial<TtsSettings>) => void] {
  const [settings, setSettings] = useState<TtsSettings>(() => loadTts());
  useEffect(() => {
    const sync = () => setSettings(loadTts());
    window.addEventListener(CHANGED, sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener(CHANGED, sync); window.removeEventListener('storage', sync); };
  }, []);
  const update = (patch: Partial<TtsSettings>) => {
    setSettings((prev) => { const next = { ...prev, ...patch }; saveTts(next); return next; });
  };
  return [settings, update];
}

// ── Voice catalogue ──────────────────────────────────────────────────────────

let cache: SpeechSynthesisVoice[] = [];
function refresh() {
  if (typeof window !== 'undefined' && window.speechSynthesis) cache = window.speechSynthesis.getVoices();
}
if (typeof window !== 'undefined' && window.speechSynthesis) {
  refresh();
  window.speechSynthesis.addEventListener?.('voiceschanged', refresh);
}

export function listVoices(): SpeechSynthesisVoice[] {
  if (cache.length === 0) refresh();
  return cache;
}

/** Reactive list of system voices (they load asynchronously on some browsers). */
export function useVoices(): SpeechSynthesisVoice[] {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>(() => listVoices());
  useEffect(() => {
    const update = () => setVoices(listVoices());
    update();
    const ss = typeof window !== 'undefined' ? window.speechSynthesis : null;
    ss?.addEventListener?.('voiceschanged', update);
    // Some engines populate late; poll a couple times.
    const t1 = setTimeout(update, 250);
    const t2 = setTimeout(update, 1000);
    return () => { ss?.removeEventListener?.('voiceschanged', update); clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return voices;
}

/** Resolve the SpeechSynthesisVoice to use given the settings + language. */
export function resolveVoice(settings: TtsSettings, lang: Language): SpeechSynthesisVoice | undefined {
  const vs = listVoices();
  if (settings.voiceURI) {
    const exact = vs.find((v) => v.voiceURI === settings.voiceURI);
    if (exact) return exact;
  }
  if (settings.voiceName) {
    const byName = vs.find((v) => v.name === settings.voiceName);
    if (byName) return byName;
  }
  const want = BCP47[lang];
  return vs.find((v) => v.lang === want)
      ?? vs.find((v) => v.lang.startsWith(lang))
      ?? vs.find((v) => v.default);
}
